import * as B from '@blueprintjs/core';
import classNames from 'classnames';
import React from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import ReactDOM from 'react-dom';

import {DataRange} from 'app/components/AnalyzePolygonChart/types';
import {getMeasurementSystem} from 'app/modules/Remote/Organization';
import {TileData} from 'app/stores/RasterCalculationStore';
import * as CONSTANTS from 'app/utils/constants';
import {isInternalOrgMemberViewingAnyOrg, useUserInfo} from 'app/utils/featureFlags';
import {makeCoordinateParts} from 'app/utils/geoJsonUtils';
import {StatusMaybe} from 'app/utils/hookUtils';
import {getLayer} from 'app/utils/layerUtils';
import * as layerUtils from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';
import {formatMeasurement} from 'app/utils/mathUtils';

import PixelInfoDebugger from './PixelInfoDebugger';
import cs from './pixelInfoStyles.module.scss';
import {MapMousePopup} from '../../MapMousePopup/MapMousePopup';

export interface PixelInfoDebugData {
  pixelColor: string | null;
  emulatedImageData: ImageData | null;
  markerCoordinates: {
    [key: string]: {x: number; y: number};
  } | null;
  rawPixelReading: number[];
  tileBounds: mapboxgl.LngLatBounds;
  tileData: StatusMaybe<TileData>;
  uncertaintyData: [lower: string, upper: string];
}

export interface ScaledPixelValue {
  displayValue: string | null;
  coords: mapboxgl.LngLat;
  dataRange: DataRange;
  uncertaintyData?: [lower: string, upper: string];
}

/** What the pinned popup hands over when the user starts a note from it. */
export interface PixelInfoNoteSeed {
  text: string;
  coords: mapboxgl.LngLat;
}

interface PixelInfoPopupContextValue {
  state: {
    isOpen: boolean;
    projectedMousePostion: mapboxgl.LngLat | null;
    scaledPixelValue: ScaledPixelValue | null;
    pixelInspectorReadyStates: {
      [key: string]: boolean;
    };
    debugData: PixelInfoDebugData | null;
    isPopupFrozen: boolean;
  };
  actions: {
    setIsOpen: (isOpen: boolean) => void;
    setProjectedMousePostion: (projectedMousePostion: mapboxgl.LngLat | null) => void;
    setScaledPixelValue: (scaledPixelValue: ScaledPixelValue | null) => void;
    setPixelInspectorReady: (panelIndex: number, isReady: boolean) => void;
    setDebugData: (debugData: PixelInfoDebugData | null) => void;
    setPanelIndex: (panelIndex: number) => void;
    setMap: (map: mapboxgl.Map | null) => void;
    togglePopupFrozen: () => void;
    setElevation: (elevation: number | null) => void;
  };
}

export const PixelInfoPopupContext = React.createContext<PixelInfoPopupContextValue>({
  state: {
    isOpen: false,
    projectedMousePostion: null,
    scaledPixelValue: null,
    pixelInspectorReadyStates: {},
    debugData: null,
    isPopupFrozen: false,
  },
  actions: {
    setIsOpen: () => {},
    setProjectedMousePostion: () => {},
    setScaledPixelValue: () => {},
    setPixelInspectorReady: () => {},
    setPanelIndex: () => {},
    setDebugData: () => {},
    setMap: () => {},
    setElevation: () => {},
    togglePopupFrozen: () => {},
  },
});

export const PixelInfoPopupProvider: React.FunctionComponent<
  React.PropsWithChildren<{
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    imageRefs: mapUtils.MapImageRefs;
    /** When set, the pinned popup offers "Add note". */
    onAddNote?: (seed: PixelInfoNoteSeed) => void;
  }>
> = ({children, isOpen, setIsOpen, imageRefs, onAddNote}) => {
  const [map, setMap] = React.useState<mapboxgl.Map | null>(null);
  const [projectedMousePostion, setProjectedMousePostion] = React.useState<mapboxgl.LngLat | null>(
    null
  );
  const [elevation, setElevation] = React.useState<number | null>(null);
  // The value of the current mouse pixel adjusted for the data range, and the raw pixel value.
  const [scaledPixelValue, setScaledPixelValue] = React.useState<ScaledPixelValue | null>(null);
  // Tracks if the pixel info tool is ready to be used, currently based on if the calculator is current.
  const [pixelInspectorReadyStates, setPixelInspectorReadyStates] = React.useState<{
    [key: string]: boolean;
  }>({});
  const appendPixelInspectorReadyState = React.useCallback(
    (panelIndex: number, isReady: boolean) => {
      setPixelInspectorReadyStates((prev) => ({...prev, [panelIndex]: isReady}));
    },
    [setPixelInspectorReadyStates]
  );
  const [panelIndex, setPanelIndex] = React.useState<number>(0);

  // Sticky ppopup tools: when the user clicks on the map, the popup will stick to the clicked
  // location until the user clicks the map again or the imageRefs change.
  const [isPopupFrozen, setPopupFrozen] = React.useState<boolean>(false);

  const togglePopupFrozen = () => {
    setPopupFrozen((prev) => !prev);
  };

  React.useEffect(() => {
    setPopupFrozen(false);
  }, [imageRefs]);

  // Debugger state tools.
  const [_, profile] = useUserInfo();
  const [debugData, setDebugData] = React.useState<PixelInfoDebugData | null>(null);

  const resetAndClosePixelInfoPopup = () => {
    setIsOpen(false);
    setPopupFrozen(false);
    setProjectedMousePostion(null);
    setScaledPixelValue(null);
    setDebugData(null);
    setElevation(null);
  };

  return (
    <PixelInfoPopupContext.Provider
      value={{
        actions: {
          setIsOpen,
          setProjectedMousePostion,
          setScaledPixelValue,
          setPixelInspectorReady: appendPixelInspectorReadyState,
          setDebugData,
          setPanelIndex,
          setMap,
          togglePopupFrozen,
          setElevation,
        },
        state: {
          isOpen,
          projectedMousePostion,
          scaledPixelValue,
          pixelInspectorReadyStates,
          debugData,
          isPopupFrozen,
        },
      }}
    >
      {children}
      {map && imageRefs[panelIndex]?.layerKey && (
        <PixelInfoPopup
          isOpen={isOpen}
          projectedMousePostion={projectedMousePostion}
          scaledPixelValue={scaledPixelValue}
          pixelInspectorReadyStates={
            isPopupFrozen
              ? Object.fromEntries(
                  Object.entries(pixelInspectorReadyStates).map(([key]) => [key, true])
                )
              : pixelInspectorReadyStates
          }
          layerKey={imageRefs[panelIndex]?.layerKey}
          panelIndex={panelIndex}
          map={map}
          mapIndex={panelIndex}
          isPopupFrozen={isPopupFrozen}
          elevation={elevation}
          closeCallback={isPopupFrozen ? () => resetAndClosePixelInfoPopup() : undefined}
          onAddNote={
            isPopupFrozen && onAddNote
              ? (seed) => {
                  resetAndClosePixelInfoPopup();
                  onAddNote(seed);
                }
              : undefined
          }
        />
      )}
      {isInternalOrgMemberViewingAnyOrg(profile) &&
        isOpen &&
        imageRefs[panelIndex]?.layerKey &&
        debugData && (
          <PixelInfoDebugger
            emulatedImageData={debugData.emulatedImageData}
            debugMarkerCoordinates={debugData.markerCoordinates}
            debugPixelColor={debugData.pixelColor}
            scaledPixelValue={scaledPixelValue}
            activeLayerKey={imageRefs[panelIndex].layerKey}
            rawPixelReading={debugData.rawPixelReading}
            uncertaintyData={debugData.uncertaintyData}
            tileBounds={debugData.tileBounds}
            tileData={debugData.tileData}
            isPixelInspectorReady={pixelInspectorReadyStates[panelIndex]}
          />
        )}
    </PixelInfoPopupContext.Provider>
  );
};

export function usePixelInfoPopup(): PixelInfoPopupContextValue {
  const value = React.useContext(PixelInfoPopupContext);
  if (!value) {
    throw new Error('usePixelInfoPopup must be beneath a PixelInfoPopupProvider');
  }
  return value;
}

const CopyableRow: React.FC<{text: string; copyText?: string | null; className?: string}> = ({
  text,
  copyText,
  className,
}) => {
  const [isCopied, setIsCopied] = React.useState(false);

  React.useEffect(() => {
    if (!isCopied) return;

    const timeoutId = window.setTimeout(() => setIsCopied(false), 1500);
    return () => window.clearTimeout(timeoutId);
  }, [isCopied]);

  return (
    <span className={classNames(className, copyText && cs.pixelInfoPopupRow)}>
      <span>{text}</span>
      {copyText && (
        <CopyToClipboard text={copyText} onCopy={(_, ok) => setIsCopied(ok)}>
          <B.Button
            small
            minimal
            className={cs.pixelInfoPopupCopyButton}
            title={`Copy ${copyText}`}
            // The popup sits inside the map container, whose click handler toggles pinning.
            onClick={(e) => e.stopPropagation()}
          >
            {isCopied ? 'Copied' : 'Copy'}
          </B.Button>
        </CopyToClipboard>
      )}
    </span>
  );
};

export const PixelInfoPopup: React.FunctionComponent<{
  isOpen: boolean;
  projectedMousePostion: mapboxgl.LngLat | null;
  scaledPixelValue: ScaledPixelValue | null;
  pixelInspectorReadyStates: {
    [key: string]: boolean;
  };
  layerKey: string | null;
  panelIndex: number;
  map: mapboxgl.Map;
  mapIndex: number;
  isPopupFrozen: boolean;
  elevation: number | null;
  closeCallback?: () => void | undefined;
  onAddNote?: (seed: PixelInfoNoteSeed) => void;
}> = ({
  isOpen,
  projectedMousePostion,
  scaledPixelValue,
  pixelInspectorReadyStates,
  layerKey,
  panelIndex,
  map,
  mapIndex,
  isPopupFrozen,
  elevation,
  closeCallback,
  onAddNote,
}) => {
  const [organization] = useUserInfo();

  const activeLayerReady = React.useMemo(() => {
    return pixelInspectorReadyStates[panelIndex];
  }, [panelIndex, pixelInspectorReadyStates]);

  const coordinateParts = React.useMemo(() => {
    const coords = scaledPixelValue?.coords;
    return coords
      ? makeCoordinateParts({type: 'Point', coordinates: [coords.lng, coords.lat]})
      : null;
  }, [scaledPixelValue]);
  const lat = coordinateParts?.lat ?? 'N/A';
  const lng = coordinateParts?.lng ?? 'N/A';

  const layerInfo = layerKey ? getLayer(layerKey) : null;
  const hasRawLayer = layerKey && layerUtils.getRawLayerKey(layerKey);

  const isLayerWithPixelValues =
    layerInfo &&
    hasRawLayer &&
    (layerInfo.type === 'data' || layerInfo?.resolutionCategory === 'data');

  const formattedElevation = React.useMemo(() => {
    if (organization && elevation !== null) {
      const measurementSystem = getMeasurementSystem(organization);
      return formatMeasurement({
        value: elevation,
        unit: CONSTANTS.UNIT_LENGTH_M,
        unitTo: measurementSystem === 'metric' ? CONSTANTS.UNIT_LENGTH_M : CONSTANTS.UNIT_LENGTH_FT,
        decimals: 2,
      }).valueWithUnit;
    }
    return null;
  }, [elevation, organization]);

  const headerElement = document.createElement('span');
  headerElement.className = cs.pixelInfoPopupHeader;
  headerElement.innerHTML = `Pixel info`;

  const bodyEl = React.useMemo(() => document.createElement('div'), []);

  let body: React.ReactNode;
  if (!activeLayerReady) {
    body = 'Loading...';
  } else {
    // Rows are collected as text too, so the note prefill can't drift from what
    // the popup shows. `noteText: null` keeps a row out of the note.
    const rows: {
      text: string;
      className?: string;
      noteText?: string | null;
      copyText?: string | null;
    }[] = [];
    if (isLayerWithPixelValues) {
      const displayValue = scaledPixelValue?.displayValue;
      rows.push({
        text: `Value: ${displayValue || 'N/A'}`,
        className: cs.pixelInfoPopupValue,
        // A note is read later without the popup's header, so name the layer.
        noteText: displayValue ? `${layerInfo.display}: ${displayValue}` : null,
        copyText: displayValue,
      });
      if (scaledPixelValue?.uncertaintyData) {
        const [lower, upper] = scaledPixelValue.uncertaintyData;
        rows.push({
          text: `90% confidence interval: ${lower} to ${upper}`,
          copyText: `${lower} to ${upper}`,
        });
      }
      if (layerInfo && 'dataRange' in layerInfo) {
        rows.push({
          text: `${layerInfo.display} range: ${layerInfo.dataRange.join(' to ')}`,
          noteText: null,
        });
      }
    }
    rows.push({text: `Lat: ${lat}`, copyText: coordinateParts?.lat});
    rows.push({text: `Long: ${lng}`, copyText: coordinateParts?.lng});
    if (formattedElevation) {
      rows.push({text: `Elevation: ${formattedElevation}`, copyText: formattedElevation});
    }

    const noteLines = rows
      .map((row) => (row.noteText === undefined ? row.text : row.noteText))
      .filter((line): line is string => !!line);

    let footerText = `Click ${isPopupFrozen ? 'on map to unpin' : 'to pin'}`;
    if (elevation == null) footerText += `, open 3D terrain to view elevation data`;

    body = (
      <div className={cs.pixelInfoPopupContentContainer}>
        {rows.map((row) => (
          <CopyableRow
            key={row.text}
            text={row.text}
            className={row.className}
            // Copying is only offered on a pinned popup: an unpinned one follows the
            // cursor, so its rows can never be hovered.
            copyText={isPopupFrozen ? row.copyText : null}
          />
        ))}
        {onAddNote && scaledPixelValue && (
          <div className={cs.pixelInfoPopupActions}>
            <B.Button
              small
              intent={B.Intent.PRIMARY}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddNote({text: noteLines.join('\n'), coords: scaledPixelValue.coords});
              }}
            >
              Add note
            </B.Button>
          </div>
        )}
        <div className={cs.pixelInfoPopupSeparator} />
        <span className={cs.pixelInfoPopupFooter}>{footerText}</span>
      </div>
    );
  }

  return (
    <>
      <MapMousePopup
        isOpen={isOpen && !!scaledPixelValue?.displayValue}
        projectedMousePostion={projectedMousePostion}
        bodyContent={bodyEl}
        headerContent={headerElement}
        map={map}
        mapIndex={mapIndex}
        closeCallback={closeCallback}
      />
      {ReactDOM.createPortal(body, bodyEl)}
    </>
  );
};
